import React, { PureComponent } from "react"
import { connect } from "react-redux"
import Engine from "json-rules-engine-simplified"
import Stepper from "react-stepper-horizontal"
import withRules from "./withRules"
import ProgressBar from "react-bootstrap/lib/ProgressBar"
import MBForm from "./mb-form"
import { submitLeadForm, uploadFile } from "@store/forms/index";

/**
 * The React runtime for DynamicForm-driven forms (contest quests, StaticContent
 * ad-hoc lead forms): wraps {@link module:./mb-form} (the react-jsonschema-form
 * renderer) with a multi-step wizard layer (stepper/progress bar, auto-advance via
 * `autoStep`, conditional exit-URLs) and dispatches submission through the Redux
 * `forms` store (`submitLeadForm`/`uploadFile` in `@store/forms`, POSTing to
 * `/forms/dynamic_form`/`/forms/upload`). Mounted via `loadComponent("FormBuilder")`
 * wherever a server-rendered page bootstraps `state.forms[id]` with the schema/UI
 * schema/rules built by `DynamicFormComponent`.
 *
 * Part of the Dynamic forms component — see docs/wiki/System/v2/leadgen/dynamic-forms.md.
 */

class StepForm extends PureComponent {
  state = { step: 0, steps: [] }

  constructor(props) {
    super(props)
    this.state.steps = this.createSteps(props.schema)
    this.anchor = null
  }

  componentWillReceiveProps(newProps) {
    if (this.props.schema !== newProps.schema) {
      this.setState({ steps: this.createSteps(newProps.schema) })
    }
  }

  createSteps(schema) {
    const steps = []
    const { uiSchema } = this.props

    if (schema.properties) {
      for (let k in schema.properties) {
        const o = schema.properties[k]
        if (o.subtype === "step") {
          steps.push([k, o, uiSchema[k]])
        }
      }
    }

    return steps
  }

  getSchemaForStep(n) {
    const { schema } = this.props
    const { properties, ...other } = schema

    const [stepId, step] = this.state.steps[n]

    return {
      ...other,
      id: stepId,
      properties: {
        [stepId]: step,
      },
    }
  }

  onStepChanged = () => {
    this.anchor && this.anchor.scrollIntoView()
  }

  previousStep = e => {
    e.preventDefault()
    e.stopPropagation()
    const { step } = this.state
    this.setState({ step: step - 1 }, this.onStepChanged)
  }

  nextStep = () => {
    const { step } = this.state
    this.setState({ step: step + 1 }, this.onStepChanged)
  }

  onChange = data => {
    this.draft = data.formData
    if (this.props.onChange) {
      this.props.onChange(data)
    }
    this.checkAutoStep(data)
  }

  checkAutoStep = data => {
    const { step, steps } = this.state
    if (step >= steps.length - 1) return

    const [stepId, stepSchema] = steps[step]

    if (!stepSchema.autoStep) return

    const isComplete = this.isStepComplete(stepId, stepSchema, data.formData)

    if (isComplete) {
      if (this.shouldExitToUrl(data.formData)) {
        return
      }
      this.nextStep()
    }
  }

  shouldExitToUrl = (formData) => {
    const exitFields = this.getExitFields()
    for (let i = 0; i < exitFields.length; i++) {
      const { stepKey, fieldKey, exitUrl, exitValue } = exitFields[i]
      const currentValue = formData[stepKey]?.[fieldKey] ?? formData[fieldKey]
      if (String(currentValue) === String(exitValue)) {
        document.location.href = exitUrl
        return true
      }
    }
    return false
  }

  isStepComplete = (stepId, stepSchema, formData) => {
    const stepData = formData[stepId] || {}
    const required = stepSchema.required || []
    const properties = stepSchema.properties || {}
    const fieldsToCheck = required.length > 0 ? required : Object.keys(properties)

    if (fieldsToCheck.length === 0) return false

    const allFilled = fieldsToCheck.every(fieldKey => {
      const value = stepData[fieldKey]
      if (value === undefined || value === null || value === '') return false
      if (Array.isArray(value) && value.length === 0) return false
      return true
    })

    return allFilled
  }

  getExitFields() {
    if (this._exitFields) return this._exitFields

    this._exitFields = []
    const { schema } = this.props
    if (!schema || !schema.properties) return this._exitFields

    for (let key in schema.properties) {
      const field = schema.properties[key]
      if (!field.properties) continue

      for (let subKey in field.properties) {
        const f = field.properties[subKey]
        if (f.exitUrlEnabled && f.exitUrl && f.exitValue !== undefined) {
          this._exitFields.push({ stepKey: key, fieldKey: subKey, exitUrl: f.exitUrl, exitValue: f.exitValue })
        }
      }
    }
    return this._exitFields
  }

  onStepSubmit = data => {
    const { step, steps } = this.state

    if (this.shouldExitToUrl(data.formData)) {
      return
    }

    // For forms without steps OR on last step
    if (steps.length === 0 || step === steps.length - 1) {
      if (this.props.onSubmit) {
        this.props.onSubmit(data)
      }
    } else if (step >= 0 && step < steps.length - 1) {
      const next = () => setTimeout(() => this.nextStep(), 1)

      if (this.state.steps[step][1].submit && this.props.onSubmit) {
        this.props.onSubmit(data, next)
      } else {
        next()
      }
    }
  }

  render() {
    const { step, steps } = this.state
    const {
      schema,
      status,
      id,
      buttonText = "Abschließen",
      nextButtonText = "Weiter",
      ...props
    } = this.props

    const isSubmitting = status === "submitting"
    const buttonStatusProps = {}
    if (isSubmitting || status === "success") {
      buttonStatusProps["disabled"] = true
    }

    if (!steps.length) {
      return (
        <MBForm {...this.props} onSubmit={this.onStepSubmit}>
          <div className="clearfix">
            <button
              className="btn btn-primary"
              type="submit"
              {...buttonStatusProps}
            >
              {isSubmitting ? "Einen Moment bitte..." : buttonText}
            </button>
          </div>
        </MBForm>
      )
    }

    const stepSchema = this.getSchemaForStep(step)

    let draft = this.props.formData || {}
    if (this.draft) {
      draft = { ...draft, ...this.draft }
    }

    let stepper = null
    // if stepper is enabled
    if (
      !props.uiSchema.hasOwnProperty("ui:stepperDisabled") ||
      !props.uiSchema["ui:stepperDisabled"]
    ) {
      const widget = props.uiSchema["ui:stepperWidget"] || "stepper"
      switch (widget) {
        case "stepper":
          stepper = (
            <Stepper
              steps={steps.map(([id, { title }, ui]) => ({
                title: (ui && ui["ui:stepperTitle"]) || title,
              }))}
              activeStep={step}
              activeColor="#9BA720"
              completeColor="#737c18"
            />
          )
          break
        case "progress":
          stepper = (
            <ProgressBar
              striped
              bsStyle="primary"
              now={((step + 1) / steps.length) * 100}
            />
          )
          break
      }
    }

    return (
      <div>
        <div className="anchor">
          <a name={`form-anchor-${id}`} ref={a => ((this.anchor = a), null)} />
        </div>

        {stepper}

        <MBForm
          {...props}
          formData={draft}
          schema={stepSchema}
          onChange={this.onChange}
          onSubmit={this.onStepSubmit}
        >
          <div className="clearfix">
            {step > 0 && (
              <div className="pull-left">
                <button
                  className="btn btn-primary"
                  onClick={this.previousStep}
                  {...buttonStatusProps}
                >
                  Zurück
                </button>
              </div>
            )}
            <div className="pull-right">
              <button
                className="btn btn-primary"
                type="submit"
                {...buttonStatusProps}
              >
                {isSubmitting
                  ? "Einen Moment bitte..."
                  : step === steps.length - 1
                  ? buttonText
                  : nextButtonText}
              </button>
            </div>
          </div>
        </MBForm>
      </div>
    )
  }
}

export const StepFormRuled = withRules(Engine)(StepForm)

/**
 * this component is only used for a static content dynamic form
 * integration. It expects that data is bootstrapped into the redux store
 * at the forms key.
 * NOTE: this form will be replaced based on the defined success-row
 * settings with a html from the server. The Replacement will happen in the redux
 * store and this component will be unmounted. Even though that might seem
 * as a huge anti pattern, i beleive it is a clean solution for now.
 */
export default connect(
    (state, { id }) => ({
        ...state.forms[id],
        id,
    }),
    (dispatch, { id }) => ({
        onSubmit: ({ formData }, eventOrNext) => {
            const next = typeof eventOrNext === "function" ? eventOrNext : undefined;
            dispatch(submitLeadForm({ id, formData }, next));
        },
        formContext: {
            actions: {
                uploadFile: file => dispatch(uploadFile({ id, file })),
            },
        },
    })
)(({ schema, ui, rules = [], status, serverError, id, buttonText, nextButtonText, ...props }) => {
    return [
        <StepFormRuled
            {...props}
            key={id}
            id={id}
            status={status}
            schema={schema}
            uiSchema={ui}
            rules={rules}
            buttonText={buttonText}
            nextButtonText={nextButtonText}
        />,
        status === "error" && serverError && (
            <div key={`server-error-${id}`} className="panel panel-danger errors gutter-top">
                <div className="panel-heading">
                    <h3 className="panel-title">{typeof serverError === "string" && (serverError.includes("|") ? serverError.split("|")[0] : "Fehler")}</h3>
                </div>
                <div className="panel-body text-danger">
                    {typeof serverError === "string" ? (
                        serverError.includes("|") ? (
                            serverError.split("|")[1]
                        ) : (
                            serverError
                        )
                    ) : (
                        <ul>
                            {Object.keys(serverError).map(k => (
                                <li key={k}>{serverError[k]}</li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        ),
    ];
});
